import React from "react";
import { MessageCircle, Share2, ThumbsDown, ThumbsUp } from "lucide-react";
import { PlatformType } from "../models/Platform";
import {
  PlatformPostFrame,
  RENDITION_PLATFORM_LABELS,
} from "./PlatformPostFrame";

interface UploadedVideoRenditionPreviewProps {
  platform: PlatformType;
  format: "video" | "story";
  channelName?: string;
  profileImage?: string | null;
  caption?: string;
  /** Video title shown in the YouTube Shorts chrome */
  title?: string;
  videoSrc: string;
}

/**
 * Drill-down preview for posts whose media is an uploaded video file. The
 * file publishes as-is, so it plays in a plain video element letterboxed
 * inside a 9:16 frame (object-contain adds the bars), wrapped in the
 * platform's chrome. Rendered by CreatePost's video branch — these posts
 * never mount ImageCompositeEditor.
 */
export function UploadedVideoRenditionPreview({
  platform,
  format,
  channelName,
  profileImage,
  caption,
  title,
  videoSrc,
}: UploadedVideoRenditionPreviewProps) {
  // Feed cards stack chrome around the frame; overlay/story chrome doesn't
  const isFeedCard =
    format !== "story" && (platform === "linkedin" || platform === "facebook");
  const heightExpr =
    platform === "youtube"
      ? "70dvh"
      : isFeedCard
        ? "min(56dvh, 80dvh - 190px)"
        : "66dvh";
  const frameStyle: React.CSSProperties = {
    height: heightExpr,
    width: `calc(${heightExpr} * ${9 / 16})`,
    maxWidth: "100%",
    aspectRatio: `${9 / 16}`,
    position: "relative",
    overflow: "hidden",
  };

  const videoFrame = (
    <div
      style={frameStyle}
      className="relative overflow-hidden flex items-center justify-center bg-black rounded-sm"
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        src={videoSrc}
        controls
        autoPlay
        loop
        playsInline
        className="w-full h-full object-contain"
      />
      {platform === "youtube" && (
        <>
          <div className="absolute inset-x-0 bottom-0 pointer-events-none">
            <div className="bg-gradient-to-t from-black/70 to-transparent px-3 pt-10 pb-12 pr-14">
              {channelName && (
                <div className="text-white text-xs font-medium mb-1">
                  @{channelName}
                </div>
              )}
              {title && (
                <div
                  className="notranslate text-white/90 text-sm leading-snug line-clamp-2"
                  translate="no"
                >
                  {title}
                </div>
              )}
            </div>
          </div>
          <div className="absolute right-2 bottom-24 pointer-events-none flex flex-col items-center gap-4 text-white/90">
            <ThumbsUp className="h-5 w-5 drop-shadow" />
            <ThumbsDown className="h-5 w-5 drop-shadow" />
            <MessageCircle className="h-5 w-5 drop-shadow" />
            <Share2 className="h-5 w-5 drop-shadow" />
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col items-center justify-center gap-2 bg-muted/20 p-4 overflow-auto animate-in fade-in slide-in-from-bottom-1 duration-300 motion-reduce:animate-none">
      <p className="text-sm text-muted-foreground text-center">
        Previewing the {RENDITION_PLATFORM_LABELS[platform]} version — changes
        apply to {RENDITION_PLATFORM_LABELS[platform]} only.
      </p>
      {platform === "youtube" ? (
        videoFrame
      ) : (
        <PlatformPostFrame
          platform={platform}
          format={format}
          channelName={channelName}
          profileImage={profileImage}
          caption={caption}
        >
          {videoFrame}
        </PlatformPostFrame>
      )}
    </div>
  );
}
